import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Form, Modal } from "react-bootstrap";
import type { User } from "firebase/auth";
import type { OrganizationRecord } from "../../../api/organizations";
import {
  grantOrganizationIntelligence,
  startOrganizationIntelligenceTrial,
} from "../../../api/organizations";
import "./IntelligenceGlass.css";

type CapacityId = "lite" | "plus" | "extra";

interface IntelligenceManageModalProps {
  show: boolean;
  onHide: () => void;
  org: OrganizationRecord | null;
  user: User;
  onUpdated: (patch: Partial<OrganizationRecord>) => void;
}

export const IntelligenceManageModal: React.FC<IntelligenceManageModalProps> = ({
  show,
  onHide,
  org,
  user,
  onUpdated,
}) => {
  const [capacity, setCapacity] = useState<string>("");
  const [price, setPrice] = useState("");
  const [allowance, setAllowance] = useState("");
  const [trialDays, setTrialDays] = useState("14");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show || !org) return;
    setCapacity(org.intelligenceCapacityId || "");
    setPrice(
      typeof org.intelligencePrice === "number" ? String(org.intelligencePrice) : "",
    );
    setAllowance(
      typeof org.intelligenceCreditsAllowance === "number"
        ? String(org.intelligenceCreditsAllowance)
        : "",
    );
    setTrialDays("14");
    setError(null);
  }, [show, org?.id]);

  const applyLocalPatch = (patch: Partial<OrganizationRecord>) => {
    onUpdated(patch);
  };

  const handleGrant = async () => {
    if (!org) return;
    setSaving(true);
    setError(null);
    try {
      const capacityId =
        capacity === "lite" || capacity === "plus" || capacity === "extra"
          ? (capacity as CapacityId)
          : null;
      const priceParsed = price ? Number(price.replace(",", ".")) : null;
      const allowanceParsed = allowance
        ? Math.max(0, parseInt(allowance, 10) || 0)
        : null;
      await grantOrganizationIntelligence(user, org.id, {
        capacityId,
        price:
          priceParsed != null && Number.isFinite(priceParsed) ? priceParsed : null,
        creditsAllowance: allowanceParsed,
        currency: "USD",
        resetCreditsUsed: true,
        trialing: false,
      });
      applyLocalPatch({
        intelligenceCapacityId: capacityId,
        intelligencePrice:
          capacityId == null
            ? null
            : priceParsed != null && Number.isFinite(priceParsed)
              ? priceParsed
              : org.intelligencePrice ?? null,
        intelligenceCreditsAllowance:
          capacityId == null
            ? null
            : allowanceParsed ?? org.intelligenceCreditsAllowance ?? null,
        intelligenceTrialing: false,
        intelligencePeriodEnd:
          capacityId == null ? null : org.intelligencePeriodEnd ?? null,
        hasUsedIntelligenceTrial: org.hasUsedIntelligenceTrial,
      });
      onHide();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update Intelligence");
    } finally {
      setSaving(false);
    }
  };

  const handleStartTrial = async () => {
    if (!org) return;
    setSaving(true);
    setError(null);
    try {
      const days = Math.max(1, Math.min(90, parseInt(trialDays, 10) || 14));
      const capacityId =
        capacity === "lite" || capacity === "plus" || capacity === "extra"
          ? (capacity as CapacityId)
          : "plus";
      const result = await startOrganizationIntelligenceTrial(user, org.id, {
        capacityId,
        trialDays: days,
        force: true,
      });
      const periodEnd = result.periodEnd ? new Date(result.periodEnd) : null;
      applyLocalPatch({
        intelligenceCapacityId: (result.capacityId as CapacityId) || capacityId,
        intelligenceTrialing: true,
        intelligencePrice: 0,
        intelligenceCreditsAllowance:
          typeof result.creditsAllowance === "number"
            ? result.creditsAllowance
            : org.intelligenceCreditsAllowance ?? null,
        intelligenceCreditsUsed: 0,
        intelligencePeriodEnd: periodEnd,
        hasUsedIntelligenceTrial: true,
      });
      onHide();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to start trial");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!org) return;
    setSaving(true);
    setError(null);
    try {
      await grantOrganizationIntelligence(user, org.id, {
        capacityId: null,
        resetCreditsUsed: true,
      });
      applyLocalPatch({
        intelligenceCapacityId: null,
        intelligencePrice: null,
        intelligenceCreditsAllowance: null,
        intelligenceCreditsUsed: null,
        intelligenceTrialing: false,
        intelligencePeriodEnd: null,
      });
      onHide();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to clear Intelligence");
    } finally {
      setSaving(false);
    }
  };

  const creditsLabel =
    typeof org?.intelligenceCreditsUsed === "number" &&
    typeof org?.intelligenceCreditsAllowance === "number"
      ? `${org.intelligenceCreditsUsed} / ${org.intelligenceCreditsAllowance}`
      : org?.intelligenceCreditsAllowance != null
        ? `— / ${org.intelligenceCreditsAllowance}`
        : null;

  return (
    <Modal show={show} onHide={() => !saving && onHide()} centered>
      <Modal.Header closeButton>
        <Modal.Title>Intelligence</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {org && (
          <>
            <p className="text-muted small mb-3">
              {org.name} ({org.id})
            </p>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {org.intelligenceCapacityId ? (
                <Badge bg="success">{org.intelligenceCapacityId.toUpperCase()}</Badge>
              ) : (
                <Badge bg="secondary">None</Badge>
              )}
              {org.intelligenceTrialing ? (
                <Badge bg="info">Trial</Badge>
              ) : null}
              {creditsLabel ? (
                <Badge bg="light" text="dark">
                  Credits {creditsLabel}
                </Badge>
              ) : null}
            </div>
            {error ? (
              <Alert variant="danger" className="py-2">
                {error}
              </Alert>
            ) : null}

            <div className="intel-glass-panel mb-3">
              <h6>Grant / adjust</h6>
              <p className="intel-glass-hint mb-3">
                Paid or complimentary capacity without Checkout. Use trial below
                for time-boxed tryouts.
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Capacity</Form.Label>
                <Form.Select
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                >
                  <option value="">None / clear</option>
                  <option value="lite">Lite</option>
                  <option value="plus">Plus</option>
                  <option value="extra">Extra</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Negotiated price (optional)</Form.Label>
                <Form.Control
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="e.g. 49.99"
                />
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>Credits allowance override (optional)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={allowance}
                  onChange={(e) => setAllowance(e.target.value)}
                />
              </Form.Group>
            </div>

            <div className="intel-glass-panel">
              <h6>Trial</h6>
              <p className="intel-glass-hint mb-3">
                Lets the org try Intelligence (default Plus, 14 days, $0). Platform
                can re-grant even after use.
              </p>
              <Form.Group className="mb-0">
                <Form.Label>Trial days</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={90}
                  value={trialDays}
                  onChange={(e) => setTrialDays(e.target.value)}
                />
              </Form.Group>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex flex-wrap gap-2">
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="outline-danger"
          onClick={handleClear}
          disabled={saving || !org?.intelligenceCapacityId}
        >
          Clear
        </Button>
        <Button
          variant="outline-primary"
          onClick={handleStartTrial}
          disabled={saving}
        >
          {saving ? "…" : "Start trial"}
        </Button>
        <Button variant="primary" onClick={handleGrant} disabled={saving}>
          {saving ? "Saving…" : "Save grant"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
